import { UntypedFormGroup, NgModel } from '@angular/forms';
import { LabelsAndData } from './types';

function removeTooltipElement(): void {
  const tooltip = document.querySelector('.tooltip');
  if (tooltip) {
    tooltip.remove();
  }
}

function setClassInvalid(form: UntypedFormGroup, prop: string) {
  return {
    'is-invalid': (!form.get(prop).pristine && !form.get(prop).valid) || (form.get(prop).touched && !form.get(prop).valid),
    'is-touched': form.get(prop).value,
    'disabled': false
  };
}

function setClassInvalidTpl(model: NgModel): object {
  return { 'is-invalid': (!model.pristine && !model.valid) || (model.touched && !model.valid) };
}

function setClassColorByStatus(status: string): object {
  return {
    'text-orange': status === 'Todo',
    'text-warning': status === 'Doing',
    'text-success': status === 'Done' || status === 'Goal achieved',
    'fa-pulse': status === 'Goal achieved',
    'text-danger': status === 'Canceled' || status === 'Not done'
  };
}

function setClassByStatus(status: string): object {
  return {
    'bg-orange': status === 'Todo',
    'badge-warning': status === 'Doing',
    'badge-success': status === 'Done' || status === 'Goal achieved',
    'fa-pulse': status === 'Goal achieved',
    'badge-danger': status === 'Canceled' || status === 'Not done'
  };
}

function setClassByStatusIcon(status: string): object {
  return {
    'fa-clock-o': status === 'Todo',
    'fa-hourglass-half': status === 'Doing',
    'fa-check': status === 'Done' || status === 'Goal achieved',
    'fa-times': status === 'Canceled' || status === 'Not done'
  };
}

function scrollTo(amount: number): void {
  window.scrollTo({ top: amount, behavior: 'smooth' });
}

function formatSource(source: string): string {
  const criteria = {
    twitter: 'X',
    camera: 'Camera',
    senato: 'Senato',
    eu: 'European Parliament',
    region: 'Regions',
    other: 'Other'
  };
  return criteria[source];
}

function addPaddingToSenatoId(personaId: string): string {
  return personaId.padStart(8, '0');
}

function capitalizeFirstChar(str: string): string {
  return `${str.charAt(0).toUpperCase()}${str.substring(1, str.length).toLowerCase()}`;
}

function toSentenceCase(str: string): string {
  return str.replace(/\w*/g, txt => `${txt.charAt(0).toUpperCase()}${txt.substring(1).toLowerCase()}`);
}

// Same as BE
function cleanText(text: string): string {
  return (text || '')
    .replace('Scarica il PDF', '')
    .replace(/\t/g, '')
    .replace(/\s\s+/g, ' ')
    .replace(/\u00a0/g, ' ')
    .replace(/\n\n+/g, '\n')
    .replace(/\u200f|\u200b|\u2066|\u2069|\u25cf/gi, '')
    .replace(/“|”|„|&#xAB;|&#xBB;|«|»|\u201c|\u201d/gi, '"')
    .replace(/’|‘|\u0060|\u2018|\u2019|\x91|\x92/g, `'`)
    .replace(/…/g, '...')
    .replace(/_+/g, '_')
    .replace(/\.+/g, '.')
    .trim();
}

function wildcardInQuotes(keyword: string, person: string, noperson: string): boolean {
  return keyword.includes('*"') || person.includes('*"') || noperson.includes('*"');
}

function parenthesisAreEven(keyword: string, person: string, noperson: string): boolean {
  // https://stackoverflow.com/questions/4009756/how-to-count-string-occurrence-in-string
  const parenthesisOpenedCountK = (keyword.match(/\(/g) || []).length;
  const parenthesisClosedCountK = (keyword.match(/\)/g) || []).length;
  const parenthesisOpenedCountP = (person.match(/\(/g) || []).length;
  const parenthesisClosedCountP = (person.match(/\)/g) || []).length;
  const parenthesisOpenedCountNP = (noperson.match(/\(/g) || []).length;
  const parenthesisClosedCountNP = (noperson.match(/\)/g) || []).length;
  return parenthesisOpenedCountK === parenthesisClosedCountK &&
    parenthesisOpenedCountP === parenthesisClosedCountP &&
    parenthesisOpenedCountNP === parenthesisClosedCountNP;
}

function doubleQuotesAreEven(keyword: string, person: string, noperson: string): boolean {
  const quotesEvenK = (keyword.match(/"/g) || []).length;
  const quotesEvenP = (person.match(/"/g) || []).length;
  const quotesEvenNP = (noperson.match(/"/g) || []).length;
  return quotesEvenK % 2 === 0 && quotesEvenP % 2 === 0 && quotesEvenNP % 2 === 0;
}

function formatNotes(str: string): string {
  return (str || '').replace(/\n/g, '<br>')
    .replace(/-? ?\[[x|X]\]/g, '<i class="fa fa-check-square text-success opacity-75 h5 m-0"></i>')
    .replace(/-? ?\[[-]\]/g, '<i class="fa fa-window-close text-danger opacity-75 h5 m-0"></i>')
    .replace(/-? ?\[ ?\]/g, '<i class="fa fa-square-o text-orange h5 m-0"></i>')
    .replace(/-? ?\([x|X]\)/g, '<i class="fa fa-dot-circle-o text-danger h5 m-0"></i>')
    .replace(/-? ?\( ?\)/g, '<i class="fa fa-circle-o text-danger h5 m-0"></i>')
    // Wrap URLs with anchor tag to make them clickable
    .replace(/(\b(https?):\/\/[-A-Z0-9+&@#/%?=~_|!:,.;]*[-A-Z0-9+&@#/%=~_|])/gim, '<a href="$1" target="_blank">$1</a>');
}

function addDaysToDate(date: Date, days: number): Date {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
}

function filterDatesInArrayByDays(array: any[], field = 'date', days: number) {
  const today = new Date();
  const xDaysAgoDate = today.setDate(today.getDate() - days);
  return array.filter(item => new Date(item[field]) >= new Date(xDaysAgoDate));
}

function isPastDate(date: Date | string): boolean {
  return new Date(date).getTime() < new Date().getTime();
}

function sortArrayByDateFieldDesc(array: any[], field: string) {
  return array.sort((a, b) => new Date(a[field]).getTime() > new Date(b[field]).getTime() ? -1 : 1);
}

function generateListItem(type: string): string {
  let symbol = '';
  let suffix = '';
  switch (type) {
    case 'todo':
      symbol = '[ ]';
      break;
    case 'done':
      symbol = '[x]';
      break;
    case 'hours':
      symbol = '\u23F1 0h';
      break;
    case 'main task':
      symbol = '( )';
      break;
    case 'main task completed':
      symbol = '(x)';
      suffix = '\uD83C\uDF89'; // Party emoji
      break;
  }
  if (type === 'hours') {
    return `${symbol} ${suffix}`;
  }
  return `\n- ${symbol} ${type} ${suffix}`;
}

function generateStatsCSV(stats: LabelsAndData, header: string): string {
  let csv = `${header}\n`;
  for (let i = 0; i < stats.data.length; i++) {
    csv += `${stats.labels[i]},${stats.data[i]}\n`;
  }
  return csv;
}

function saveFile(res: any, name: string): void {
  const binaryData = [];
  binaryData.push(res);
  const downloadLink = document.createElement('a');
  downloadLink.href = window.URL.createObjectURL(new Blob(binaryData, { type: res.type }));
  downloadLink.setAttribute('download', name);
  document.body.appendChild(downloadLink);
  downloadLink.click();
  downloadLink.parentNode.removeChild(downloadLink);
}

export {
  removeTooltipElement,
  setClassInvalid,
  setClassInvalidTpl,
  setClassColorByStatus,
  setClassByStatus,
  setClassByStatusIcon,
  scrollTo,
  formatSource,
  addPaddingToSenatoId,
  capitalizeFirstChar,
  toSentenceCase,
  cleanText,
  wildcardInQuotes,
  parenthesisAreEven,
  doubleQuotesAreEven,
  formatNotes,
  addDaysToDate,
  filterDatesInArrayByDays,
  isPastDate,
  sortArrayByDateFieldDesc,
  generateListItem,
  generateStatsCSV,
  saveFile,
};
